import { FormHandler, FormSection } from '@framework/form';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { AnyObject, yup, YupAnyObjectSchema } from '@framework/validation';
import {
  AdvancedSearchFormSection,
  IAdvancedSearchFormSectionProps,
} from './component/AdvancedSearchFormSection';
import { AdvancedSearchQueryValuesSetter } from './component/AdvancedSearchQueryValuesSetter';

export interface AdvancedSearchProps<
  TSearchable extends AnyObject,
  TQuery extends TSearchable,
> extends IAdvancedSearchFormSectionProps {
  schema: yup.ObjectSchema<TSearchable>;
  setQuery: (values: TQuery) => void;
  transform?: {
    format: (values: TSearchable) => TQuery;
    parse: (values: TQuery) => TSearchable;
  };
  query: TQuery;
  defaultQuery: TQuery;
  /**
   * Passando null si disabilita la paginazione.
   */
  pageKey: keyof TQuery | null;
  /**
   * Indice della pagina di default. 0 di default.
   */
  pageDefaultIndex?: number;
}

const AdvancedSearchWrapper = styled.div``;

/**
 * Riga della ricerca con tre colonne: select, input e button
 */
export function AdvancedSearch<
  TQuery extends TSearchable,
  TSearchable extends AnyObject = Record<string, any>,
>(props: AdvancedSearchProps<TSearchable, TQuery>) {
  const {
    id,
    fields,
    defaultFieldName,
    extraActions,
    setQuery,
    schema,
    query,
    pageKey,
    pageDefaultIndex = 0,
    transform,
    defaultQuery,
  } = props;

  const handleSearch = useCallback(
    (values: TSearchable | null) => {
      if (!values) {
        setQuery({ ...defaultQuery });
        return;
      }

      setQuery({
        ...(transform?.format ? transform.format(values) : values),
        ...(pageKey ? { [pageKey]: pageDefaultIndex } : {}),
      } as TQuery);
    },
    [defaultQuery, pageDefaultIndex, pageKey, setQuery, transform],
  );
  const defaultInitialValues = useMemo(() => ({}), []);

  const parsedQuery = useMemo(() => {
    return transform?.parse ? transform.parse(query) : query;
  }, [query, transform]);

  return (
    <FormHandler
      nested
      schema={schema as YupAnyObjectSchema}
      initialValues={defaultInitialValues}
      onReset={() => {
        handleSearch(null);
      }}
      hideVisualFeedback
      onSubmit={async values => {
        try {
          handleSearch(values as TSearchable);
        } catch (e) {
          console.error(`[Errore] Errore nella ricerca`, e);
        }
      }}
    >
      <AdvancedSearchQueryValuesSetter
        query={parsedQuery}
        schema={schema as YupAnyObjectSchema}
      />
      <FormSection removeHelpMargin>
        <AdvancedSearchWrapper>
          {/* TODO {...divProps} */}
          <AdvancedSearchFormSection
            id={id}
            fields={fields}
            query={parsedQuery}
            defaultFieldName={defaultFieldName}
            extraActions={extraActions}
          />
        </AdvancedSearchWrapper>
      </FormSection>
    </FormHandler>
  );
}
